/**
 * Invoice List Page
 * ==================
 * Lists all invoices with extracted data from OCR/GPT processing.
 * Uses DataDashboard for a full-featured table view.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataDashboard } from '../../shared/components';
import { useAuth } from '../../shared/context/AuthContext';
import { useLang } from '../../shared/i18n';
import { config } from '../../utils/config';
import documentApi, { DocumentItem } from '../../services/documentApi';
import InvoiceExportModal, { DateRange } from './InvoiceExportModal';
import BulkUploadModal from './BulkUploadModal';
import { exportInvoicesToExcel } from '../../utils/invoiceExport';
import { getInvoiceColumns } from './columns';
import './InvoiceList.scss';

// Navigation icons as SVG components
const DashboardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);

const InvoiceIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);

const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const InvoiceList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { t } = useLang();

    const [data, setData] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false
    });

    const [isEditing, setIsEditing] = useState(false);

    // Export modal state
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Upload modal state
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // Navigation items for sidebar
    const navItems = useMemo(() => [
        {
            id: 'rfq-invoices',
            label: t('navRfqInvoices'),
            icon: <DashboardIcon />,
            route: '/'
        },
        {
            id: 'invoices',
            label: t('navAllInvoices'),
            icon: <InvoiceIcon />,
            route: '/invoices'
        },
        {
            id: 'separator-1',
            label: '',
            icon: <></>,
            isSeparator: true
        },
        {
            id: 'settings',
            label: t('navSettings'),
            icon: <SettingsIcon />,
            route: '/settings'
        }
    ], [t]);

    // Sidebar configuration
    const sidebarConfig = useMemo(() => ({
        logo: 'Invoice Management',
        navItems,
        currentRoute: location.pathname + location.search,
        onNavigate: (route: string) => navigate(route),
        user: {
            name: user?.name || 'User',
            email: user?.email || ''
        },
        onLogout: () => {
            logout();
            window.location.href = config.PORTAL_URL;
        }
    }), [navItems, location.pathname, location.search, navigate, user, logout]);

    // Split docs: processing (pending/processing) vs ready (completed/failed)
    const processingDocs = useMemo(() =>
        data.filter(d => d.ocr_status === 'pending' || d.ocr_status === 'processing'), [data]);
    const readyDocs = useMemo(() =>
        data.filter(d => d.ocr_status !== 'pending' && d.ocr_status !== 'processing'), [data]);

    // Flatten nested data for sorting/filtering to work
    const flattenedData = useMemo(() => {
        return readyDocs.map(item => ({
            ...item,
            vendor_name: item.extracted_data?.vendor?.name || item.extracted_data?.supplier_name || '',
            supplier_tax_id: item.extracted_data?.supplier_tax_id || item.extracted_data?.vendor?.tax_id || '',
            supplier_address: item.extracted_data?.supplier_address || item.extracted_data?.vendor?.address || '',
            invoice_number: item.extracted_data?.invoice_number || '',
            invoice_date: item.extracted_data?.invoice_date || '',
            due_date: item.extracted_data?.due_date || '',
            total_amount: item.extracted_data?.financials?.total_amount || item.extracted_data?.total_amount || 0,
            currency: item.extracted_data?.financials?.currency || item.extracted_data?.currency || '',
            total_tax_amount: item.extracted_data?.total_tax_amount || item.extracted_data?.financials?.tax || 0,
            net_amount: item.extracted_data?.net_amount || item.extracted_data?.financials?.subtotal || 0,
            line_items_count: item.extracted_data?.line_items?.length || item.extracted_data?.items?.length || 0,
            expense_category: (item as any).expense_category || item.extracted_data?.expense_category || ''
        }));
    }, [readyDocs]);

    // Polling refs
    const pollingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reprocessTriggered = useRef(false);
    const mountedRef = useRef(true);

    const fetchData = useCallback(async (params: any = {}) => {
        setLoading(true);
        setError(null);
        try {
            const page = params.page || 1;
            const limit = params.limit || 20;
            const response = await documentApi.getDocuments(page, limit, 'invoice');

            if (response.success && response.data) {
                const docs = response.data.documents;
                setData(docs);
                setPagination({
                    page: response.data.page,
                    limit: response.data.page_size,
                    total: response.data.total,
                    pages: Math.ceil(response.data.total / response.data.page_size),
                    has_next: response.data.has_next,
                    has_prev: response.data.has_prev
                });

                // Auto-poll when pending/processing docs exist
                const hasPending = docs.some(
                    (d: DocumentItem) => d.ocr_status === 'pending' || d.ocr_status === 'processing'
                );
                if (hasPending) {
                    if (!reprocessTriggered.current) {
                        reprocessTriggered.current = true;
                        documentApi.reprocessPending().catch(() => {});
                    }
                    if (pollingTimer.current) clearTimeout(pollingTimer.current);
                    pollingTimer.current = setTimeout(() => {
                        if (mountedRef.current) fetchData({ page, limit });
                    }, 3000);
                }
            } else {
                setError(response.message || 'Failed to fetch invoices');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        fetchData();
        return () => {
            mountedRef.current = false;
            if (pollingTimer.current) clearTimeout(pollingTimer.current);
        };
    }, [fetchData]);

    const handleDataFetch = (newParams: any) => {
        fetchData(newParams);
    };

    const formatCurrency = (amount?: number, currency?: string): string => {
        if (amount === undefined || amount === null) return '-';
        const curr = currency || 'TRY';
        try {
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: curr }).format(amount);
        } catch {
            return `${amount.toLocaleString('tr-TR')} ${curr}`;
        }
    };

    const columns = useMemo(() => getInvoiceColumns(formatCurrency, t), [t]);

    const handleSaveChanges = useCallback(async (changes: Record<string, Record<string, any>>) => {
        try {
            const promises = Object.entries(changes).map(([docId, fields]) =>
                documentApi.updateDocumentFields(docId, fields)
            );
            await Promise.all(promises);
            setIsEditing(false);
            fetchData({ page: pagination.page, limit: pagination.limit });
        } catch (err) {
            console.error('Failed to save changes:', err);
        }
    }, [fetchData, pagination]);

    const handleView = useCallback(() => {
        if (selectedIds.length === 1) navigate(`/invoices/${selectedIds[0]}`);
    }, [selectedIds, navigate]);

    // Fetch all invoices for export
    const fetchAllInvoicesForExport = async (): Promise<DocumentItem[]> => {
        const allInvoices: DocumentItem[] = [];
        let currentPage = 1;
        let hasMore = true;
        const pageSize = 100;

        while (hasMore) {
            const response = await documentApi.getDocuments(currentPage, pageSize, 'invoice');
            if (response.success && response.data) {
                allInvoices.push(...response.data.documents);
                hasMore = response.data.has_next;
                currentPage++;
            } else {
                hasMore = false;
            }
        }

        return allInvoices;
    };

    const handleExportClick = () => {
        setIsExportModalOpen(true);
    };

    const handleUploadClick = () => {
        setIsUploadModalOpen(true);
    };

    const handleUploadComplete = () => {
        fetchData({ page: 1, limit: 20 });
    };

    const handleExportConfirm = async (dateRange: DateRange) => {
        setIsExporting(true);
        try {
            const allInvoices = await fetchAllInvoicesForExport();
            exportInvoicesToExcel(allInvoices, {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });
            setIsExportModalOpen(false);
        } catch (err) {
            console.error('Export failed:', err);
            alert(t('exportError'));
        } finally {
            setIsExporting(false);
        }
    };

    const processingBanner = processingDocs.length > 0 ? (
        <div className="processing-banner">
            <div className="processing-banner__header">
                <div className="processing-banner__spinner" />
                <span className="processing-banner__title">
                    {t('processingCount').replace('{count}', String(processingDocs.length))}
                </span>
            </div>
            <div className="processing-banner__files">
                {processingDocs.map(doc => (
                    <div key={doc.id} className="processing-banner__file">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="processing-banner__filename">{doc.filename}</span>
                        <span className={`processing-banner__status processing-banner__status--${doc.ocr_status}`}>
                            {doc.ocr_status === 'processing' ? t('statusProcessing') : t('statusPending')}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    ) : null;

    // Custom header actions for toolbar
    const customHeaderActions = (
        <>
            <button
                className="action-button action-button-secondary"
                onClick={handleView}
                disabled={selectedIds.length !== 1}
                title={selectedIds.length !== 1 ? t('viewTooltipSingle') : t('viewTooltipReady')}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
                {t('view')}
            </button>
            <button
                className="action-button action-button-primary"
                onClick={handleUploadClick}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t('bulkUpload')}
            </button>
        </>
    );

    return (
        <>
            <DataDashboard
                title={t('allInvoicesTitle')}
                pageDescription={t('allInvoicesDescription')}
                collection="invoices"
                data={flattenedData}
                columns={columns}
                isLoading={loading}
                error={error}
                onSelectionChange={setSelectedIds}
                onDataFetch={handleDataFetch}
                onExport={handleExportClick}
                paginationInfo={pagination}
                showAddButton={false}
                showEditButton={true}
                showDeleteButton={false}
                showExportButton={true}
                isEditing={isEditing}
                onEditToggle={() => setIsEditing(prev => !prev)}
                onSaveChanges={handleSaveChanges}
                customHeaderActions={customHeaderActions}
                topContent={processingBanner}
                sidebarConfig={sidebarConfig}
            />
            <InvoiceExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleExportConfirm}
                isLoading={isExporting}
            />
            <BulkUploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUploadComplete={handleUploadComplete}
            />
        </>
    );
};

export default InvoiceList;
